'use client';

import { memo } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import { Node } from '@headless-workspace/typings';
import { cva } from 'class-variance-authority';
import { GlowIcon, GlowTextField, GlowTextBody, GlowTextCaption, Icons, PropsWithClassName } from '../../../../src';

export type CheckboxType = 'default' | 'error';
export type CheckboxPosition = 'left' | 'right';

const containerClasses = cva('flex items-center gap-0.5 w-full', {
    variants: {
        position: {
            left: 'flex-row',
            right: 'flex-row-reverse justify-end',
        },
        cursor: {
            noPointer: '',
            default: 'cursor-pointer',
        },
    },
    defaultVariants: {
        position: 'left',
    },
});

const checkboxClasses = cva(
    'flex w-checkbox h-checkbox rounded-0.25 text-text-primary-inverse items-center justify-center border-solid border-bold',
    {
        variants: {
            state: {
                default: 'border-border-brand',
                error: 'border-border-error',
            },
            checked: {
                true: 'bg-surface-brand border-background-brand',
                false: '',
            },
        },
        defaultVariants: {
            state: 'default',
            checked: false,
        },
    },
);

export type GlowCheckboxProps = PropsWithClassName & {
    label?: string | number;
    checked: boolean;
    onChange?: () => void;
    name?: string;
    id: string;
    type?: CheckboxType;
    position?: CheckboxPosition;
    helperText?: string;
    warningText?: string;
    isRequired?: boolean;
    disabled?: boolean;
    labelElement?: Node;
};

export const GlowCheckbox: ComponentMemo<GlowCheckboxProps> = memo(
    ({
        label,
        labelElement,
        checked = false,
        onChange,
        name,
        id,
        type = 'default',
        position = 'left',
        helperText,
        warningText,
        isRequired = false,
        className,
        disabled = false,
        ...props
    }) => {
        const checkboxState = checked ? 'default' : type;
        const cursor = disabled ? 'noPointer' : 'default';
        const isErrorState = type === 'error' && !checked;
        const isDefaultState = type === 'default';

        const shouldShowWarning = !!warningText && isErrorState;
        const shouldShowHelperText = !!helperText && isDefaultState;

        const supportTextElement = shouldShowWarning ? (
            <div className={'flex flex-row gap-0.25 fill-text-error'}>
                {shouldShowWarning && <GlowIcon Icon={Icons.Warning} type={'small'} />}
                <GlowTextCaption
                    text={warningText}
                    color={'error'}
                    TagName={'span'}
                    className={'relative top-[0.1rem]'}
                />
            </div>
        ) : shouldShowHelperText ? (
            <GlowTextCaption text={helperText} color={'primary'} TagName={'span'} />
        ) : null;

        return (
            <div
                className={
                    'w-fit flex justify-center flex-col gap-0.5 has-[:focus-visible]:outline has-[:focus-visible]:rounded-0.25'
                }
            >
                <label htmlFor={id} className={clsx(containerClasses({ position, cursor }), className)}>
                    <GlowTextField
                        type={'checkbox'}
                        id={id}
                        name={name}
                        checked={checked}
                        onChange={onChange}
                        className={'sr-only'}
                        required={isRequired}
                        disabled={disabled}
                        {...props}
                    />
                    <span
                        className={clsx(
                            checkboxClasses({ state: checkboxState, checked }),
                            className,
                            !checked && 'hover:bg-surface-primary-hover',
                            disabled && 'bg-background-disabled border-border-disabled',
                        )}
                    >
                        {checked && (
                            <GlowIcon Icon={Icons.Vector} type={'medium'} className={'text-text-primary-inverse'} />
                        )}
                    </span>
                    {labelElement ?? <GlowTextBody text={label} TagName={'span'} />}
                </label>
                {supportTextElement}
            </div>
        );
    },
);

GlowCheckbox.displayName = 'GlowCheckbox';
